import type {
  AccountDetail,
  AccountDirectoryData,
  AccountDirectoryRecord,
  CostLimitsData,
  DeviceRegistryDetail,
  OperationsData,
  OverviewData,
  PublishCostLimitsRequest,
  PublishCostLimitsResponse,
  UsageData,
  WorkspaceDetail,
  WorkspaceDirectoryData,
  WorkspaceDirectoryRecord,
} from "../types";

const now = () => Date.now();

const DEMO_DIRECTORY_PAGE_SIZE = 20;

const demoAccounts: AccountDirectoryRecord[] = Array.from({ length: 47 }, (_, index) => ({
  id: `demo_account_${String(index + 1).padStart(3, "0")}`,
  email: `user${String(index + 1).padStart(2, "0")}@example.com`,
  displayName: index % 4 === 0 ? null : `Demo Kullanıcı ${index + 1}`,
  disabled: index === 13 || index === 36,
  emailVerified: index % 5 !== 0,
  createdAt: now() - (index + 12) * 86_400_000,
  lastSignInAt: index % 9 === 0 ? null : now() - (index + 1) * 3_600_000,
  providerIds: index % 3 === 0 ? ["google.com"] : ["password"],
}));

const demoWorkspaces: WorkspaceDirectoryRecord[] = Array.from({ length: 43 }, (_, index) => ({
  id: `${index % 4 === 0 ? "team" : "personal"}_demo_${String(index + 1).padStart(3, "0")}`,
  name: index % 6 === 0 ? null : index % 4 === 0 ? `Demo Ekibi ${index + 1}` : `Kişisel Alan ${index + 1}`,
  ownerId: `demo_account_${String((index % 25) + 1).padStart(3, "0")}`,
  kind: index % 4 === 0 ? "team" : "personal",
  status: index === 17 ? "suspended" : "active",
  createdAt: now() - (index + 20) * 86_400_000,
}));

const parseDemoCursor = (cursor?: string): number => {
  const offset = Number(cursor ?? 0);
  return Number.isSafeInteger(offset) && offset >= 0 ? offset : 0;
};

export const demoAccountDirectory = (cursor?: string): AccountDirectoryData => {
  const offset = parseDemoCursor(cursor);
  const records = demoAccounts.slice(offset, offset + DEMO_DIRECTORY_PAGE_SIZE);
  const nextOffset = offset + records.length;
  return {
    generatedAt: now(),
    pageSize: DEMO_DIRECTORY_PAGE_SIZE,
    nextCursor: nextOffset < demoAccounts.length ? String(nextOffset) : null,
    records,
  };
};

export const demoWorkspaceDirectory = (cursor?: string): WorkspaceDirectoryData => {
  const offset = parseDemoCursor(cursor);
  const records = demoWorkspaces.slice(offset, offset + DEMO_DIRECTORY_PAGE_SIZE);
  const nextOffset = offset + records.length;
  return {
    generatedAt: now(),
    pageSize: DEMO_DIRECTORY_PAGE_SIZE,
    nextCursor: nextOffset < demoWorkspaces.length ? String(nextOffset) : null,
    records,
  };
};

let demoCostLimits: CostLimitsData = {
  schemaVersion: 1,
  revision: 3,
  source: "published",
  updatedAt: now() - 2 * 86_400_000,
  updatedBy: "demo-platform-admin",
  requestId: "11111111-1111-4111-8111-111111111111",
  limits: {
    accountCostNanoUsdPerMonth: 100_000_000,
    accountRequestsPerDay: 20,
    accountRequestsPerMinute: 2,
    accountTokensPerMonth: 250_000,
    backboneCostNanoUsdPerDay: 1_000_000_000,
    projectCostNanoUsdPerDay: 250_000_000,
    projectRequestsPerDay: 2_000,
  },
};

export const demoCostLimitsData = (): CostLimitsData => structuredClone(demoCostLimits);

export const publishDemoCostLimits = (request: PublishCostLimitsRequest): PublishCostLimitsResponse => {
  if (request.expectedRevision !== demoCostLimits.revision) throw new Error("Politika başka bir oturumda değişti. Yenileyip tekrar deneyin.");
  demoCostLimits = {
    schemaVersion: 1,
    revision: demoCostLimits.revision + 1,
    source: "published",
    updatedAt: now(),
    updatedBy: "demo-platform-admin",
    requestId: request.requestId,
    limits: structuredClone(request.limits),
  };
  return { revision: demoCostLimits.revision };
};

export const demoOverview = (): OverviewData => ({
  generatedAt: now(),
  metrics: [
    { id: "activeAccounts", label: "Aktif hesap", value: 1842, isLowerBound: false, status: "healthy", detail: "Sunucuya ait aktif hesap kayıtları" },
    { id: "activeWorkspaces", label: "Aktif workspace", value: 1976, isLowerBound: false, status: "healthy", detail: "Kişisel ve ekip alanları" },
    { id: "teamWorkspaces", label: "Ekip workspace'i", value: 134, isLowerBound: false, status: "healthy", detail: "Birden fazla üye barındırabilen alanlar" },
    { id: "deviceRegistries", label: "Cihaz kaydı", value: 619, isLowerBound: true, status: "healthy", detail: "En az 619 üye-cihaz kaydı" },
    { id: "subscriptionRecords", label: "Mağaza kaydı", value: 86, isLowerBound: false, status: "healthy", detail: "Apple ve Google doğrulanmış kayıtları" },
    { id: "attention", label: "Dikkat gereken", value: 2, isLowerBound: false, status: "attention", detail: "Kapasite ve servis uyarıları" },
  ],
  notices: [
    { id: "retention", title: "Sayaç temizliği kapalı", description: "Kullanım defteri korunuyor; retention silme işlemi etkin değil.", status: "attention" },
    { id: "billing", title: "Mağaza route'u kapalı", description: "Paket kayıtları okunabilir; istemci satın alma route'u henüz kullanıma açık değil.", status: "attention" },
    { id: "firestore", title: "Doğrudan Firestore erişimi kapalı", description: "Panel veriyi yalnız yetkili sunucu fonksiyonu üzerinden okur.", status: "healthy" },
  ],
});

export const demoAccount = (): AccountDetail => ({
  id: "native_pilot_20260910",
  email: "pilot@tayan.app",
  createdAt: now() - 18 * 86_400_000,
  lastSignInAt: now() - 2 * 3_600_000,
  status: "active",
  planId: "free",
  planExpiresAt: null,
  billingRevision: 3,
  hasMoreWorkspaces: false,
  workspaces: [
    { id: "personal_7d3d2ab91e", kind: "personal", name: "Kişisel Alan", role: "owner", status: "active" },
    { id: "team_northstar", kind: "team", name: "Northstar Analytics", role: "admin", status: "active" },
  ],
  subscription: {
    key: "google_4bbcd9",
    provider: "google",
    environment: "production",
    state: "inactive",
    expiresAt: now() - 5 * 86_400_000,
    replacedBy: null,
  },
});

export const demoWorkspace = (): WorkspaceDetail => ({
  id: "team_northstar",
  name: "Northstar Analytics",
  kind: "team",
  ownerId: "owner_northstar_01",
  ownerEmail: "owner@northstar.example",
  status: "active",
  createdAt: now() - 64 * 86_400_000,
  memberCount: 4,
  hasMoreMembers: false,
  connectionCount: 6,
  sharedAnalysisCount: 18,
  members: [
    { userId: "owner_northstar_01", email: "owner@northstar.example", role: "owner", status: "active" },
    { userId: "admin_northstar_02", email: "admin@northstar.example", role: "admin", status: "active" },
    { userId: "editor_northstar_03", email: "analyst@northstar.example", role: "editor", status: "active" },
    { userId: "viewer_northstar_04", email: "viewer@northstar.example", role: "viewer", status: "active" },
  ],
  infrastructure: {
    revision: 7,
    sourceId: "workspace-postgres",
    modelId: "gpt-5-mini",
    dataBoundary: "approved-cloud",
    retentionDays: 30,
  },
});

export const demoDevices = (): DeviceRegistryDetail => ({
  accountId: "owner_northstar_01",
  workspaceId: "team_northstar",
  userId: "admin_northstar_02",
  devices: [
    { deviceId: "macbook-pro-kaya", appId: "app.tayan.desktop", status: "active", proofIssuedAt: now() - 2 * 86_400_000, registrationExpiresAt: now() + 28 * 86_400_000 },
    { deviceId: "mac-studio-office", appId: "app.tayan.desktop", status: "revoked", proofIssuedAt: now() - 42 * 86_400_000, registrationExpiresAt: now() - 12 * 86_400_000 },
  ],
});

export const demoUsage = (): UsageData => ({
  generatedAt: now(),
  emergencyDisabled: false,
  retentionEnabled: false,
  capacity: { records: 1729, bytes: 1_710_024, warning: false },
  policies: [
    {
      planId: "free",
      analysesPerMinute: demoCostLimits.limits.accountRequestsPerMinute,
      analysesPerDay: demoCostLimits.limits.accountRequestsPerDay,
      tokenLimitPerMonth: demoCostLimits.limits.accountTokensPerMonth,
      costLimitNanoUsdPerMonth: demoCostLimits.limits.accountCostNanoUsdPerMonth,
      maxPromptTokens: 32_768,
      maxCompletionTokens: 1024,
    },
  ],
  note: "Canlı sayaç görünümü yalnız Tayan Counter Worker'ın yetkili özet sözleşmesinden alınır; ham bucket kimlikleri ve kullanıcı içerikleri panele taşınmaz.",
});

export const demoOperations = (): OperationsData => ({
  generatedAt: now(),
  services: [
    { id: "api", name: "Tayan API", status: "healthy", detail: "Korunan API istek kabul ediyor", checkedAt: now() },
    { id: "edge", name: "Cloudflare API Edge", status: "healthy", detail: "İmzalı upstream geçidi kullanılabilir", checkedAt: now() },
    { id: "hosting", name: "Web uygulaması", status: "healthy", detail: "my.tayan.app erişilebilir", checkedAt: now() },
    { id: "counter", name: "Kullanım sayaçları", status: "attention", detail: "Çalışıyor; retention kapalı", checkedAt: now() },
    { id: "desktopRelease", name: "Masaüstü yayını", status: "healthy", detail: "Üç platform manifesti yayımlı", checkedAt: now() },
  ],
  desktopRelease: { version: "0.1.0", buildNumber: "1", publishedAt: now() - 86_400_000, platforms: ["macOS arm64", "Windows x64", "Linux x64"] },
});
